import { AlertTriangle, Mail } from 'lucide-react';

const SUPPORT_EMAIL = import.meta.env.VITE_SUPPORT_EMAIL || 'support@coastal-harvest.com';

interface ErrorNoticeProps {
  message: string;
  // Opaque correlation id returned by the backend; safe to share with support.
  errorId?: string;
  // Optional context shown in the report email subject (e.g. an order id).
  orderId?: string;
  className?: string;
}

/**
 * A friendly, consistent error panel. Shows a clear message and — when the
 * backend supplied a correlation id — a "Report this problem" button that opens
 * the user's mail client pre-filled with ONLY the reference id and order id (no
 * raw error/payment details), so support can trace it in the server logs
 * without any sensitive data leaving the browser.
 */
export function ErrorNotice({ message, errorId, orderId, className = '' }: ErrorNoticeProps) {
  const subject = `Checkout problem${orderId ? ` (order ${orderId})` : ''}`;
  const bodyLines = [
    'Hi Coastal Harvest team,',
    '',
    'I ran into a problem while checking out:',
    `  "${message}"`,
    '',
    ...(errorId ? [`Reference ID: ${errorId}`] : []),
    ...(orderId ? [`Order ID: ${orderId}`] : []),
    '',
    'Thanks!',
  ];
  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines.join('\n'))}`;

  return (
    <div className={`rounded-lg border border-red-200 bg-red-50 p-4 ${className}`} role="alert">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-red-800">{message}</p>
          {errorId && (
            <p className="text-xs text-red-500 mt-1 font-mono break-all">Reference ID: {errorId}</p>
          )}
          <a
            href={mailto}
            className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-red-700 hover:text-red-900 hover:underline"
          >
            <Mail className="w-4 h-4" /> Report this problem
          </a>
        </div>
      </div>
    </div>
  );
}
